import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Users,
    UserFocus,
    CurrencyCircleDollar,
    Sparkle,
    ArrowsClockwise,
    FilePdf,
    Trash,
    ArrowUp,
    ArrowDown,
    Prohibit,
    Eye,
    FileText,
    CheckCircle,
    Warning,
    CaretDown,
    Target,
    ChartLineUp,
    Trophy,
    ArrowUUpLeft,
    Clock,
} from "@phosphor-icons/react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { AdsImportBar } from "@/components/AdsImportBar";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    adsDemographics,
    adsDemoRecommend,
    adsDemoApply,
    adsDemoApplied,
    adsListCampaigns,
    adsDemoUnapplyAll,
    adsDemoImpact,
    adsDemoImpactStatus,
    adsDemoImpactRevert, errText,
} from "@/lib/api";

const DIM_META = {
    age: { label: "Age", icon: Users, order: ["18-24", "25-34", "35-44", "45-54", "55-64", "65+", "Undetermined"] },
    gender: { label: "Gender", icon: UserFocus, order: ["Female", "Male", "Undetermined"] },
    income: {
        label: "Household Income",
        icon: CurrencyCircleDollar,
        order: ["Top 10%", "11-20%", "21-30%", "31-40%", "41-50%", "Below 50%", "Undetermined"],
    },
};

const ACTION_META = {
    increase_bid: { icon: ArrowUp, label: "Increase Bid", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
    decrease_bid: { icon: ArrowDown, label: "Decrease Bid", cls: "bg-amber-100 text-amber-900 border-amber-300" },
    exclude: { icon: Prohibit, label: "Exclude", cls: "bg-red-100 text-red-900 border-red-300" },
    monitor: { icon: Eye, label: "Monitor", cls: "bg-sky-100 text-sky-900 border-sky-300" },
};

const orderBuckets = (summary, dim) => {
    const preferred = DIM_META[dim]?.order || [];
    return [...summary].sort((a, b) => {
        const ai = preferred.indexOf(a.bucket);
        const bi = preferred.indexOf(b.bucket);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return (b.cost || 0) - (a.cost || 0);
    });
};

// Re-aggregate demographic `rows` client-side, filtered to a specific
// campaign_id when one is selected. Mirrors the server-side `_agg_by_key`
// logic so the "All campaigns" view matches whatever the API returned.
const aggregateRows = (rows) => {
    const bucket = new Map();
    for (const r of rows || []) {
        const k = r.bucket || "Undetermined";
        const b =
            bucket.get(k) ||
            { bucket: k, impressions: 0, clicks: 0, cost: 0, conversions: 0 };
        b.impressions += r.impressions || 0;
        b.clicks += r.clicks || 0;
        b.cost += r.cost || 0;
        b.conversions += r.conversions || 0;
        bucket.set(k, b);
    }
    return Array.from(bucket.values()).map((b) => ({
        bucket: b.bucket,
        impressions: b.impressions,
        clicks: b.clicks,
        cost: Math.round(b.cost * 100) / 100,
        conversions: Math.round(b.conversions * 100) / 100,
        ctr: b.impressions > 0 ? Math.round((b.clicks / b.impressions) * 10000) / 100 : 0,
        conv_rate: b.clicks > 0 ? Math.round((b.conversions / b.clicks) * 10000) / 100 : 0,
        cost_per_conv:
            b.conversions > 0 ? Math.round((b.cost / b.conversions) * 100) / 100 : null,
    }));
};

export const DemographicsAuditor = () => {
    const [data, setData] = useState(null); // { age:{summary,rows}, gender, income, customer_id, date_range, account_name }
    const [loading, setLoading] = useState(false);
    const [recs, setRecs] = useState([]);
    const [recsLoading, setRecsLoading] = useState(false);
    // Applied recommendations, keyed as `${dimension}::${bucket}::${campaign_id||all}`
    const [appliedMap, setAppliedMap] = useState({});
    // Pending apply request key while awaiting server response.
    const [applyingKey, setApplyingKey] = useState(null);
    // Recommendation queued for user confirmation. Shape: { rec, key, index }
    const [confirmRec, setConfirmRec] = useState(null);
    // Whether the "Clear Applied History" confirmation dialog is open.
    const [confirmClearAll, setConfirmClearAll] = useState(false);
    const [clearingAll, setClearingAll] = useState(false);
    // Enabled Search campaigns for the loaded account (populated after fetch).
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    // Selected campaign_id — "" means "All campaigns".
    const [selectedCampaign, setSelectedCampaign] = useState("");
    // Guards against out-of-order AI-rec responses when the user clicks the
    // dropdown multiple times quickly.
    const recRequestId = useRef(0);

    // Optimization Performance Tracker state — list of sync events with
    // pre/post metrics fetched fresh from Google Ads each time.
    const [impactItems, setImpactItems] = useState([]);
    const [impactLoading, setImpactLoading] = useState(false);
    const [impactRevertingId, setImpactRevertingId] = useState(null);
    const [impactMarkingId, setImpactMarkingId] = useState(null);
    const [confirmRevert, setConfirmRevert] = useState(null); // full impact row
    const [impactExpanded, setImpactExpanded] = useState(true);

    const buildKey = (dim, bucket, campaignId) =>
        `${(dim || "").toLowerCase()}::${(bucket || "").trim().toLowerCase()}::${
            campaignId || "all"
        }`;

    const refreshApplied = async (customerId) => {
        if (!customerId) return;
        try {
            const r = await adsDemoApplied(customerId);
            const next = {};
            (r?.items || []).forEach((it) => {
                next[buildKey(it.dimension, it.bucket, it.campaign_id)] = it;
            });
            setAppliedMap(next);
        } catch {
            /* silent — badges just won't show */
        }
    };

    // Pull the Optimization Performance Tracker feed. Runs live GA queries
    // per sync event so pre/post windows are always fresh. Kept separate
    // from `refreshApplied` (which only reads DB) because Impact is heavy.
    const refreshImpact = async (customerId) => {
        if (!customerId) return;
        setImpactLoading(true);
        try {
            const r = await adsDemoImpact(customerId);
            setImpactItems(r?.items || []);
        } catch (e) {
            const detail = errText(e, "Failed");
            toast.error("Couldn't load Impact tracker", { description: detail });
            setImpactItems([]);
        } finally {
            setImpactLoading(false);
        }
    };

    const runRecommendations = (summaries, meta) => {
        const rid = ++recRequestId.current;
        setRecs([]);
        setRecsLoading(true);
        adsDemoRecommend(summaries.age || [], summaries.gender || [], summaries.income || [], meta)
            .then((r) => {
                if (rid === recRequestId.current) {
                    setRecs(r?.recommendations || []);
                }
            })
            .catch(() => {
                if (rid === recRequestId.current) setRecs([]);
            })
            .finally(() => {
                if (rid === recRequestId.current) setRecsLoading(false);
            });
    };

    const handleFetch = async (customerId, dateRange, account) => {
        setLoading(true);
        try {
            const d = await adsDemographics(customerId, dateRange, account?.name || "");
            const nextData = {
                ...d,
                account_name: account?.name || `Account ${customerId}`,
                currency: account?.currency || "",
            };
            setData(nextData);
            setSelectedCampaign("");
            setAppliedMap({});
            // Fire recommendations + applied history + campaign list + impact
            // tracker in parallel.
            refreshApplied(customerId);
            refreshImpact(customerId);
            runRecommendations(
                {
                    age: nextData.age?.summary || [],
                    gender: nextData.gender?.summary || [],
                    income: nextData.income?.summary || [],
                },
                { account_name: nextData.account_name, date_range: dateRange }
            );
            // Populate the campaign picker with campaigns that actually have
            // demographic data in the returned rows (union across dims) — that
            // way the dropdown only shows campaigns the user can meaningfully
            // filter to.
            setCampaigns([]);
            setCampaignsLoading(true);
            adsListCampaigns(customerId)
                .then((r) => {
                    const withData = new Set();
                    ["age", "gender", "income"].forEach((dim) => {
                        (nextData[dim]?.rows || []).forEach((row) => {
                            if (row.campaign_id) withData.add(row.campaign_id);
                        });
                    });
                    const list = (r?.campaigns || []).filter(
                        (c) => c.status === "ENABLED" && c.channel === "SEARCH"
                    );
                    // Sort: campaigns with data first, then alphabetical.
                    list.sort((a, b) => {
                        const ad = withData.has(a.id) ? 0 : 1;
                        const bd = withData.has(b.id) ? 0 : 1;
                        if (ad !== bd) return ad - bd;
                        return (a.name || "").localeCompare(b.name || "");
                    });
                    setCampaigns(
                        list.map((c) => ({ ...c, has_data: withData.has(c.id) }))
                    );
                })
                .catch(() => setCampaigns([]))
                .finally(() => setCampaignsLoading(false));
        } finally {
            setLoading(false);
        }
    };

    // Filtered data for the currently selected campaign. When "All campaigns"
    // is selected we render the server-side summary as-is; when a specific
    // campaign is selected we re-aggregate the raw rows on the client.
    const filteredData = useMemo(() => {
        if (!data) return null;
        if (!selectedCampaign) return data;
        const filterRows = (rows) =>
            (rows || []).filter((r) => (r.campaign_id || "") === selectedCampaign);
        const ageRows = filterRows(data.age?.rows);
        const genderRows = filterRows(data.gender?.rows);
        const incomeRows = filterRows(data.income?.rows);
        return {
            ...data,
            age: { summary: aggregateRows(ageRows), rows: ageRows },
            gender: { summary: aggregateRows(genderRows), rows: genderRows },
            income: { summary: aggregateRows(incomeRows), rows: incomeRows },
        };
    }, [data, selectedCampaign]);

    // Whenever the campaign filter changes, re-run AI recommendations against
    // the newly-filtered summaries so the reasons + suggested bid % adapt to
    // the campaign's actual performance profile.
    useEffect(() => {
        if (!data || !filteredData) return;
        // Skip the initial render — the recs from handleFetch already cover
        // the "All campaigns" case.
        if (!selectedCampaign) return;
        runRecommendations(
            {
                age: filteredData.age?.summary || [],
                gender: filteredData.gender?.summary || [],
                income: filteredData.income?.summary || [],
            },
            {
                account_name:
                    (data.account_name || "") +
                    (selectedCampaign
                        ? ` · ${
                              campaigns.find((c) => c.id === selectedCampaign)?.name ||
                              selectedCampaign
                          }`
                        : ""),
                date_range: data.date_range || "",
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaign]);

    const clearAll = () => {
        setData(null);
        setRecs([]);
        setAppliedMap({});
        setCampaigns([]);
        setSelectedCampaign("");
    };

    const requestApply = (rec, index) => {
        if (!rec || rec.action === "monitor") return;
        const key = buildKey(rec.dimension, rec.bucket, selectedCampaign);
        if (appliedMap[key]) return; // already applied — no-op
        setConfirmRec({ rec, key, index });
    };

    const downloadCsvFallback = (csv, rec) => {
        try {
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `demo_apply_${rec.dimension}_${(rec.bucket || "bucket").replace(/[^a-z0-9]+/gi, "-")}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            /* ignore */
        }
    };

    const confirmClearAllApplied = async () => {
        if (!data?.customer_id) return;
        setClearingAll(true);
        try {
            const resp = await adsDemoUnapplyAll(data.customer_id, false);
            setAppliedMap({});
            toast.success(
                `Cleared ${resp.removed} applied record${resp.removed === 1 ? "" : "s"}`,
                {
                    description:
                        "All recommendations are actionable again. Note: this does NOT reverse any live Google Ads changes.",
                    duration: 8000,
                }
            );
        } catch (e) {
            toast.error("Failed to clear applied history", {
                description:
                    errText(e, "Unknown error"),
            });
        } finally {
            setClearingAll(false);
            setConfirmClearAll(false);
        }
    };

    const confirmApply = async () => {
        if (!confirmRec || !data?.customer_id) return;
        const { rec, key } = confirmRec;
        setApplyingKey(key);
        setConfirmRec(null);
        const campaignName = selectedCampaign
            ? campaigns.find((c) => c.id === selectedCampaign)?.name || ""
            : "";
        try {
            const resp = await adsDemoApply({
                customer_id: data.customer_id,
                dimension: rec.dimension,
                bucket: rec.bucket,
                action: rec.action,
                suggested_bid_pct: rec.suggested_bid_pct || 0,
                account_name: data.account_name || "",
                campaign_id: selectedCampaign || null,
                campaign_name: campaignName,
            });
            // Mark as applied regardless of live-push status (server persists).
            setAppliedMap((prev) => ({ ...prev, [key]: resp.applied }));
            // Refresh the Impact tracker so the new sync event shows up
            // immediately (with baseline captured, post-metrics pending).
            refreshImpact(data.customer_id);
            const scopeSuffix = selectedCampaign ? ` · ${campaignName}` : "";
            if (resp.pushed_live) {
                toast.success(
                    `Applied to ${resp.campaigns_updated} campaign${resp.campaigns_updated === 1 ? "" : "s"}`,
                    { description: `${rec.bucket} · ${rec.dimension}${scopeSuffix}` }
                );
            } else {
                // Fallback CSV path — surface the exact Google Ads error so the
                // user can diagnose permission / field issues.
                if (resp.csv_fallback) downloadCsvFallback(resp.csv_fallback, rec);
                toast.warning("Google Ads push blocked — CSV downloaded", {
                    description:
                        resp.push_error ||
                        "Import the CSV in Google Ads Editor to apply this change.",
                    duration: 12000,
                });
            }
        } catch (e) {
            const status = e?.response?.status;
            const detail =
                errText(e, "Failed to apply recommendation");
            if (status === 409) {
                await refreshApplied(data.customer_id);
                toast.info(detail);
            } else {
                toast.error(`Apply failed${status ? ` (${status})` : ""}`, {
                    description:
                        typeof detail === "string"
                            ? detail
                            : JSON.stringify(detail),
                    duration: 12000,
                });
            }
        } finally {
            setApplyingKey(null);
        }
    };

    const handleExportPdf = () => {
        if (!filteredData) {
            toast.error("Fetch data first");
            return;
        }
        const view = filteredData;
        const scopeName = selectedCampaign
            ? campaigns.find((c) => c.id === selectedCampaign)?.name ||
              `Campaign ${selectedCampaign}`
            : "All campaigns";
        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const marginX = 40;
        let y = 48;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(10, 10, 10);
        doc.text("Demographics & Income Audit", marginX, y);
        y += 8;
        doc.setDrawColor(0, 47, 167);
        doc.setLineWidth(3);
        doc.line(marginX, y, marginX + 160, y);
        y += 22;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(
            `${view.account_name} · ${scopeName} · ${view.date_range?.replace(/_/g, " ").toLowerCase()} · Generated ${new Date().toLocaleString()}`,
            marginX,
            y
        );
        y += 20;

        // AI Recommendations block
        if (recs.length) {
            doc.setTextColor(10, 10, 10);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("AI Recommendations", marginX, y);
            y += 8;
            autoTable(doc, {
                startY: y,
                margin: { left: marginX, right: marginX },
                head: [["Dimension", "Bucket", "Action", "Bid Δ%", "Reason"]],
                body: recs.map((r) => [
                    (r.dimension || "").toUpperCase(),
                    r.bucket,
                    ACTION_META[r.action]?.label || r.action,
                    r.suggested_bid_pct
                        ? (r.action === "decrease_bid" ? "-" : "+") + Math.abs(r.suggested_bid_pct) + "%"
                        : "—",
                    r.reason,
                ]),
                styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
                headStyles: { fillColor: [0, 47, 167], textColor: 255, fontStyle: "bold" },
                alternateRowStyles: { fillColor: [250, 250, 250] },
                columnStyles: {
                    0: { cellWidth: 60 },
                    1: { cellWidth: 80 },
                    2: { cellWidth: 80 },
                    3: { cellWidth: 50, halign: "right" },
                    4: { cellWidth: "auto" },
                },
            });
            y = doc.lastAutoTable.finalY + 20;
        }

        // Per-dimension tables
        ["age", "gender", "income"].forEach((dim) => {
            const summary = orderBuckets(view[dim]?.summary || [], dim);
            if (!summary.length) return;
            if (y > 640) {
                doc.addPage();
                y = 48;
            }
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.setTextColor(10, 10, 10);
            doc.text(DIM_META[dim].label, marginX, y);
            y += 8;
            autoTable(doc, {
                startY: y,
                margin: { left: marginX, right: marginX },
                head: [["Bucket", "Impr.", "Clicks", "CTR %", "Cost", "Conv.", "Conv Rate %", "Cost/Conv"]],
                body: summary.map((r) => [
                    r.bucket,
                    r.impressions,
                    r.clicks,
                    r.ctr.toFixed(1),
                    r.cost.toFixed(2),
                    r.conversions.toFixed(1),
                    r.conv_rate.toFixed(1),
                    r.cost_per_conv != null ? r.cost_per_conv.toFixed(2) : "—",
                ]),
                styles: { fontSize: 8, cellPadding: 4 },
                headStyles: { fillColor: [10, 10, 10], textColor: 255, fontStyle: "bold" },
                alternateRowStyles: { fillColor: [250, 250, 250] },
            });
            y = doc.lastAutoTable.finalY + 20;
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(140, 140, 140);
            doc.text(
                `SERP-Scout · Demographics Audit · Page ${i} / ${pageCount}`,
                marginX,
                doc.internal.pageSize.getHeight() - 20
            );
        }
        doc.save(`demographics_audit_${Date.now()}.pdf`);
        toast.success("PDF downloaded");
    };

    const totals = useMemo(() => {
        if (!filteredData) return null;
        const all = [
            ...(filteredData.age?.summary || []),
            ...(filteredData.gender?.summary || []),
        ];
        // Use ONE dimension only to avoid double-counting.
        const dim = filteredData.age?.summary?.length
            ? filteredData.age.summary
            : filteredData.gender?.summary || [];
        const spend = dim.reduce((s, r) => s + r.cost, 0);
        const conv = dim.reduce((s, r) => s + r.conversions, 0);
        return {
            spend,
            conv,
            cpa: conv > 0 ? spend / conv : null,
            hasData: all.length > 0,
        };
    }, [filteredData]);

    const selectedCampaignName = selectedCampaign
        ? campaigns.find((c) => c.id === selectedCampaign)?.name ||
          `Campaign ${selectedCampaign}`
        : "";

    return (
        <div className="flex-1 overflow-y-auto thin-scroll bg-white">
            <div className="max-w-7xl mx-auto p-6 lg:p-10">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="mono-label">// performance · demographics</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-ink mt-1">
                            Demographics & Income Insights
                        </h1>
                        <p className="text-sm text-neutral-600 mt-2 max-w-3xl">
                            Age · Gender · Household Income performance for any client account under your MCC — with AI-generated bid & exclusion recommendations.
                        </p>
                    </div>
                    {data && (
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="btn-demo-download-pdf"
                                onClick={handleExportPdf}
                                className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <FilePdf size={13} weight="bold" />
                                Save as PDF
                            </button>
                            <button
                                data-testid="btn-demo-clear"
                                onClick={clearAll}
                                className="brutal-btn border border-neutral-400 bg-white text-neutral-700 font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                            >
                                <Trash size={13} weight="bold" />
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                <AdsImportBar reportKind="demographics" onFetch={handleFetch} />

                {data && (
                    <CampaignPicker
                        campaigns={campaigns}
                        loading={campaignsLoading}
                        value={selectedCampaign}
                        onChange={setSelectedCampaign}
                        accountName={data.account_name}
                        appliedCount={Object.keys(appliedMap).length}
                        onClearAllApplied={() => setConfirmClearAll(true)}
                        clearingAll={clearingAll}
                    />
                )}

                {loading && (
                    <div className="border border-neutral-300 bg-neutral-50 p-8 text-center">
                        <ArrowsClockwise
                            size={26}
                            weight="bold"
                            className="text-klein animate-spin mx-auto"
                        />
                        <div className="mt-3 font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                            Pulling demographics from Google Ads…
                        </div>
                    </div>
                )}

                {data && totals && (
                    <div className="border-2 border-ink bg-ink text-white p-4 flex flex-wrap items-center justify-between gap-4 mb-8">
                        <div className="flex items-center gap-3">
                            <FileText size={18} weight="bold" />
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[10px] text-neutral-400">
                                    Loaded
                                </div>
                                <div className="font-heading font-black text-sm">
                                    {data.account_name}{" "}
                                    <span className="text-neutral-400 font-mono">
                                        · {data.date_range?.replace(/_/g, " ").toLowerCase()}
                                    </span>
                                    {selectedCampaignName && (
                                        <span className="ml-2 inline-flex items-center gap-1 border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[9px] font-bold px-1.5 py-0.5">
                                            <Target size={9} weight="bold" />
                                            {selectedCampaignName}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <StatCell label="Total Spend" value={totals.spend.toFixed(2)} />
                            <StatCell label="Total Conv." value={totals.conv.toFixed(1)} />
                            <StatCell
                                label="Blended CPA"
                                value={totals.cpa != null ? totals.cpa.toFixed(2) : "—"}
                                highlight
                            />
                        </div>
                    </div>
                )}

                {data && (
                    <RecommendationsPanel
                        recs={recs}
                        loading={recsLoading}
                        appliedMap={appliedMap}
                        applyingKey={applyingKey}
                        onApply={requestApply}
                        buildKey={buildKey}
                        selectedCampaign={selectedCampaign}
                    />
                )}

                {data && (
                    <ImpactTrackerPanel
                        items={impactItems}
                        loading={impactLoading}
                        expanded={impactExpanded}
                        onToggleExpanded={() => setImpactExpanded((v) => !v)}
                        onRefresh={() => refreshImpact(data.customer_id)}
                        revertingId={impactRevertingId}
                        markingId={impactMarkingId}
                        currency={data.currency}
                        onMarkStatus={async (id, status) => {
                            setImpactMarkingId(id);
                            try {
                                await adsDemoImpactStatus(id, status);
                                toast.success(
                                    status === "winner"
                                        ? "Marked as Winner"
                                        : "Status cleared",
                                );
                                await refreshImpact(data.customer_id);
                            } catch (e) {
                                const detail =
                                    errText(e, "Failed to update status");
                                toast.error(detail);
                            } finally {
                                setImpactMarkingId(null);
                            }
                        }}
                        onRequestRevert={(row) => setConfirmRevert(row)}
                    />
                )}

                {data && (
                    <div className="grid grid-cols-1 gap-8">
                        {["age", "gender", "income"].map((dim) => (
                            <DimensionSection
                                key={dim}
                                dim={dim}
                                data={filteredData?.[dim]}
                                recs={recs.filter((r) => r.dimension === dim)}
                                appliedMap={appliedMap}
                                buildKey={buildKey}
                                selectedCampaign={selectedCampaign}
                            />
                        ))}
                    </div>
                )}
            </div>

            <AlertDialog
                open={confirmClearAll}
                onOpenChange={(o) => !clearingAll && setConfirmClearAll(o)}
            >
                <AlertDialogContent data-testid="demo-clear-applied-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-xl font-black uppercase tracking-tight text-ink">
                            Clear applied history?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-neutral-700 space-y-3">
                                <div>
                                    This removes{" "}
                                    <span className="font-bold text-ink">
                                        {Object.keys(appliedMap).length}
                                    </span>{" "}
                                    applied record
                                    {Object.keys(appliedMap).length === 1 ? "" : "s"} for{" "}
                                    <span className="font-bold text-ink">
                                        {data?.account_name}
                                    </span>{" "}
                                    from our database so every recommendation becomes
                                    actionable again.
                                </div>
                                <div className="bg-amber-50 border border-amber-400 p-3 text-xs leading-relaxed">
                                    <span className="font-bold text-amber-800">
                                        Important:
                                    </span>{" "}
                                    this does <span className="underline">not</span>{" "}
                                    reverse anything that was pushed live to Google Ads.
                                    Only the "Applied" badges are cleared so you can
                                    re-run the apply flow (useful after we shipped the
                                    live-push fix).
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="btn-clear-applied-cancel"
                            disabled={clearingAll}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="btn-clear-applied-confirm"
                            onClick={confirmClearAllApplied}
                            disabled={clearingAll}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {clearingAll ? "Clearing…" : "Yes, clear all"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!confirmRec}
                onOpenChange={(o) => !o && setConfirmRec(null)}
            >
                <AlertDialogContent data-testid="demo-apply-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-xl font-black uppercase tracking-tight text-ink">
                            Apply Recommendation?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-neutral-700 space-y-3">
                                {confirmRec && (
                                    <>
                                        <div>
                                            {confirmRec.rec.action === "exclude" ? (
                                                <>
                                                    Are you sure you want to{" "}
                                                    <span className="font-bold text-red-700">
                                                        exclude
                                                    </span>{" "}
                                                    <span className="font-bold text-ink">
                                                        {confirmRec.rec.bucket}
                                                    </span>{" "}
                                                    ({confirmRec.rec.dimension}) from{" "}
                                                    {selectedCampaignName
                                                        ? "this campaign"
                                                        : "all enabled Search campaigns"}
                                                    ?
                                                </>
                                            ) : (
                                                <>
                                                    Apply a{" "}
                                                    <span
                                                        className={`font-bold ${
                                                            confirmRec.rec.action ===
                                                            "increase_bid"
                                                                ? "text-emerald-700"
                                                                : "text-amber-700"
                                                        }`}
                                                    >
                                                        {confirmRec.rec.action ===
                                                        "increase_bid"
                                                            ? "+"
                                                            : "-"}
                                                        {Math.abs(
                                                            confirmRec.rec
                                                                .suggested_bid_pct || 25
                                                        )}
                                                        %{" "}
                                                        {confirmRec.rec.action ===
                                                        "increase_bid"
                                                            ? "increase"
                                                            : "decrease"}
                                                    </span>{" "}
                                                    to{" "}
                                                    <span className="font-bold text-ink">
                                                        {confirmRec.rec.bucket}
                                                    </span>{" "}
                                                    ({confirmRec.rec.dimension})
                                                    {selectedCampaignName
                                                        ? " on this campaign"
                                                        : " across all enabled Search campaigns"}
                                                    ?
                                                </>
                                            )}
                                        </div>
                                        <div className="bg-neutral-100 border border-neutral-300 p-3 text-xs space-y-1">
                                            <div>
                                                <div className="font-mono uppercase tracking-widest text-[9px] font-bold text-neutral-600">
                                                    Account
                                                </div>
                                                <div className="font-heading font-black text-ink">
                                                    {data?.account_name}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="font-mono uppercase tracking-widest text-[9px] font-bold text-neutral-600">
                                                    Scope
                                                </div>
                                                <div className="font-heading font-black text-ink">
                                                    {selectedCampaignName ||
                                                        "All enabled Search campaigns"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-xs text-neutral-500 leading-relaxed">
                                            This writes a campaign-level criterion via the
                                            Google Ads API. If your developer token lacks
                                            write permission, we'll download a Google Ads
                                            Editor CSV and surface the exact API error so
                                            you can diagnose it.
                                        </div>
                                    </>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-demo-apply-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="btn-demo-apply-confirm"
                            onClick={confirmApply}
                            className="bg-klein hover:bg-klein/90 text-white"
                        >
                            Yes, Apply
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!confirmRevert}
                onOpenChange={(o) => !impactRevertingId && !o && setConfirmRevert(null)}
            >
                <AlertDialogContent data-testid="demo-revert-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading text-xl font-black uppercase tracking-tight text-ink">
                            Revert this change?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-sm text-neutral-700 space-y-3">
                                {confirmRevert && (
                                    <>
                                        <div>
                                            This will call the Google Ads API and{" "}
                                            {confirmRevert.action === "exclude" ? (
                                                <>
                                                    <span className="font-bold">
                                                        remove the exclusion
                                                    </span>{" "}
                                                    on{" "}
                                                    <span className="font-bold text-ink">
                                                        {confirmRevert.bucket}
                                                    </span>{" "}
                                                    ({confirmRevert.dimension}), letting
                                                    ads show to this audience again.
                                                </>
                                            ) : (
                                                <>
                                                    <span className="font-bold">
                                                        reset the bid modifier to 0%
                                                    </span>{" "}
                                                    on{" "}
                                                    <span className="font-bold text-ink">
                                                        {confirmRevert.bucket}
                                                    </span>{" "}
                                                    ({confirmRevert.dimension}) across
                                                    every ad group that received the
                                                    original change.
                                                </>
                                            )}
                                        </div>
                                        <div className="border border-neutral-300 bg-neutral-50 p-3 text-xs text-neutral-600 leading-relaxed">
                                            Scope:{" "}
                                            <span className="font-bold text-ink">
                                                {confirmRevert.campaign_name ||
                                                    (confirmRevert.campaign_id
                                                        ? `Campaign ${confirmRevert.campaign_id}`
                                                        : "All enabled Search campaigns")}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="btn-demo-revert-cancel"
                            disabled={!!impactRevertingId}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="btn-demo-revert-confirm"
                            disabled={!!impactRevertingId}
                            onClick={async () => {
                                if (!confirmRevert) return;
                                const id = confirmRevert.id;
                                setImpactRevertingId(id);
                                try {
                                    const resp = await adsDemoImpactRevert(id);
                                    if (resp.pushed_live) {
                                        toast.success("Change reverted on Google Ads");
                                    } else {
                                        toast.warning(
                                            resp.revert_error ||
                                                "Marked reverted, but nothing pushed live",
                                            { duration: 12000 },
                                        );
                                    }
                                    setConfirmRevert(null);
                                    await refreshImpact(data.customer_id);
                                    // Also refresh applied badges — the record
                                    // is still there but its status changed.
                                    await refreshApplied(data.customer_id);
                                } catch (e) {
                                    const detail =
                                        errText(e, "Revert failed");
                                    toast.error(detail, { duration: 12000 });
                                } finally {
                                    setImpactRevertingId(null);
                                }
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {impactRevertingId ? "Reverting…" : "Yes, revert on Google Ads"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const StatCell = ({ label, value, highlight }) => (
    <div>
        <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-400 mb-1">
            {label}
        </div>
        <div className={`font-heading font-black text-sm ${highlight ? "text-sky-300" : ""}`}>
            {value}
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Optimization Performance Tracker — pre/post comparison for every sync
// event. Reads live post-sync metrics from `/ads/demographics/impact`
// (which the parent refreshes after every apply). Every row shows:
//   • Baseline (stored 14-day pre-sync window)
//   • Post-sync live metrics (from applied_at to today)
//   • CPA delta % (LOWER = better)
//   • Conv Rate delta % (HIGHER = better)
//   • Status pill (Pending / Winner / Reverted)
//   • Actions: mark Winner / one-click Revert
// ---------------------------------------------------------------------------
const fmt2 = (v) => (v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(2));
const fmtPct = (v) => (v == null || Number.isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`);

const DeltaBadge = ({ pct, invert }) => {
    if (pct == null) {
        return (
            <span className="font-mono uppercase tracking-widest text-[9px] font-bold text-neutral-400">
                —
            </span>
        );
    }
    const value = Number(pct);
    // For CPA (invert=true), a NEGATIVE delta is a WIN (cost went down).
    // For conv rate, POSITIVE delta is a win. Colour accordingly.
    const isWin = invert ? value < 0 : value > 0;
    const isLoss = invert ? value > 0 : value < 0;
    const sign = value > 0 ? "+" : "";
    const colour = isWin
        ? "bg-emerald-600 text-white"
        : isLoss
        ? "bg-red-600 text-white"
        : "bg-neutral-300 text-neutral-800";
    return (
        <span
            className={`inline-flex items-center gap-1 ${colour} font-mono uppercase tracking-widest text-[10px] font-bold px-1.5 py-0.5`}
        >
            {sign}
            {value.toFixed(1)}%
        </span>
    );
};

const StatusPill = ({ status }) => {
    const meta = {
        pending: { label: "Pending", cls: "border border-neutral-400 bg-white text-neutral-700" },
        winner: { label: "Winner", cls: "bg-emerald-600 text-white" },
        reverted: { label: "Reverted", cls: "bg-red-600 text-white line-through opacity-80" },
    }[status] || { label: status, cls: "border border-neutral-400 bg-white text-neutral-700" };
    return (
        <span
            className={`inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] font-bold px-1.5 py-0.5 ${meta.cls}`}
        >
            {meta.label}
        </span>
    );
};

const ImpactTrackerPanel = ({
    items,
    loading,
    expanded,
    onToggleExpanded,
    onRefresh,
    revertingId,
    markingId,
    currency,
    onMarkStatus,
    onRequestRevert,
}) => {
    const winners = items.filter((i) => i.status === "winner").length;
    const reverted = items.filter((i) => i.status === "reverted").length;
    const pending = items.filter((i) => i.status === "pending").length;
    const currencyCode = (currency || "").toUpperCase();
    return (
        <div className="border-2 border-ink bg-white mb-8" data-testid="impact-tracker-panel">
            <button
                type="button"
                onClick={onToggleExpanded}
                className="w-full flex items-center justify-between gap-4 px-4 py-3 bg-ink text-white"
                data-testid="impact-tracker-toggle"
            >
                <div className="flex items-center gap-3">
                    <ChartLineUp size={18} weight="bold" />
                    <div className="text-left">
                        <div className="font-mono uppercase tracking-widest text-[10px] text-neutral-400">
                            Optimization Performance Tracker
                        </div>
                        <div className="font-heading font-black text-sm">
                            {items.length}{" "}
                            {items.length === 1 ? "sync event" : "sync events"}
                            {items.length > 0 && (
                                <span className="text-neutral-400 font-mono ml-2">
                                    · {pending} pending · {winners} winner
                                    {winners === 1 ? "" : "s"} · {reverted} reverted
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRefresh();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.stopPropagation();
                                onRefresh();
                            }
                        }}
                        data-testid="impact-tracker-refresh"
                        title="Re-fetch post-sync metrics from Google Ads"
                        className="brutal-btn border border-neutral-400 bg-white text-neutral-700 font-mono uppercase tracking-widest text-[10px] font-bold px-2 h-7 flex items-center gap-1 cursor-pointer"
                    >
                        <ArrowsClockwise
                            size={11}
                            weight="bold"
                            className={loading ? "animate-spin" : ""}
                        />
                        Refresh
                    </span>
                    <CaretDown
                        size={14}
                        weight="bold"
                        className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                </div>
            </button>

            {expanded && (
                <div>
                    {loading && !items.length && (
                        <div className="p-8 text-center">
                            <ArrowsClockwise
                                size={22}
                                weight="bold"
                                className="text-klein animate-spin mx-auto"
                            />
                            <div className="mt-3 font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                                Loading impact data from Google Ads…
                            </div>
                        </div>
                    )}
                    {!loading && !items.length && (
                        <div className="p-6 text-center text-sm text-neutral-500">
                            No sync events yet — apply a recommendation above to start tracking impact.
                        </div>
                    )}
                    {items.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="impact-tracker-table">
                                <thead className="bg-neutral-100 border-b-2 border-ink text-neutral-700">
                                    <tr className="font-mono uppercase tracking-widest text-[10px]">
                                        <th className="text-left px-3 py-2">Date</th>
                                        <th className="text-left px-3 py-2">Change</th>
                                        <th className="text-left px-3 py-2">Scope</th>
                                        <th className="text-right px-3 py-2">Pre CPA (14d)</th>
                                        <th className="text-right px-3 py-2">Post CPA</th>
                                        <th className="text-right px-3 py-2">Δ CPA</th>
                                        <th className="text-right px-3 py-2">Pre Conv Rate</th>
                                        <th className="text-right px-3 py-2">Post Conv Rate</th>
                                        <th className="text-right px-3 py-2">Δ Conv Rate</th>
                                        <th className="text-center px-3 py-2">Status</th>
                                        <th className="text-right px-3 py-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((row) => {
                                        const actionMeta = ACTION_META[row.action] || {
                                            label: row.action,
                                        };
                                        const bidLabel = row.suggested_bid_pct
                                            ? `${row.action === "decrease_bid" ? "-" : row.action === "increase_bid" ? "+" : ""}${Math.abs(row.suggested_bid_pct)}%`
                                            : "";
                                        const scope =
                                            row.campaign_name ||
                                            (row.campaign_id
                                                ? `Campaign ${row.campaign_id}`
                                                : `All Search campaigns (${row.campaign_names?.length || 0})`);
                                        const isReverting = revertingId === row.id;
                                        const isMarking = markingId === row.id;
                                        const isReverted = row.status === "reverted";
                                        const isWinner = row.status === "winner";
                                        const post = row.post || {};
                                        const baseline = row.baseline || {};
                                        const postReady = post && post.captured_at;
                                        const daysSince = row.days_since ?? 0;
                                        return (
                                            <tr
                                                key={row.id}
                                                className={`border-b border-neutral-200 ${isReverted ? "opacity-60" : ""}`}
                                                data-testid={`impact-row-${row.id}`}
                                            >
                                                <td className="px-3 py-2 font-mono text-[11px] text-neutral-600 whitespace-nowrap">
                                                    {(row.applied_at || "").slice(0, 10)}
                                                    <div className="text-[9px] text-neutral-400 uppercase tracking-widest">
                                                        {daysSince === 0
                                                            ? "today"
                                                            : `${daysSince}d ago`}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="font-heading font-black text-ink text-[13px] capitalize">
                                                        {row.dimension} · {row.bucket}
                                                    </div>
                                                    <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-500">
                                                        {actionMeta.label}
                                                        {bidLabel ? ` · ${bidLabel}` : ""}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-neutral-700 text-[12px]">
                                                    {scope}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-[12px] text-neutral-700">
                                                    {baseline.cpa != null
                                                        ? `${currencyCode ? currencyCode + " " : ""}${fmt2(baseline.cpa)}`
                                                        : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-[12px] text-neutral-700">
                                                    {postReady ? (
                                                        post.cpa != null ? (
                                                            `${currencyCode ? currencyCode + " " : ""}${fmt2(post.cpa)}`
                                                        ) : (
                                                            "—"
                                                        )
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-neutral-400">
                                                            <Clock size={11} weight="bold" />
                                                            Gathering
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <DeltaBadge
                                                        pct={row.cpa_delta_pct}
                                                        invert
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-[12px] text-neutral-700">
                                                    {fmtPct(baseline.conv_rate)}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-[12px] text-neutral-700">
                                                    {postReady
                                                        ? fmtPct(post.conv_rate)
                                                        : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <DeltaBadge
                                                        pct={row.conv_rate_delta_pct}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <StatusPill status={row.status} />
                                                </td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    <div className="inline-flex items-center gap-1">
                                                        {!isReverted && (
                                                            <button
                                                                type="button"
                                                                data-testid={`impact-mark-winner-${row.id}`}
                                                                disabled={isMarking || isReverting}
                                                                onClick={() =>
                                                                    onMarkStatus(
                                                                        row.id,
                                                                        isWinner
                                                                            ? "pending"
                                                                            : "winner",
                                                                    )
                                                                }
                                                                title={
                                                                    isWinner
                                                                        ? "Clear Winner mark"
                                                                        : "Mark as Winner (keep)"
                                                                }
                                                                className={`brutal-btn font-mono uppercase tracking-widest text-[10px] font-bold px-2 h-7 flex items-center gap-1 ${
                                                                    isWinner
                                                                        ? "bg-emerald-600 text-white border border-emerald-700"
                                                                        : "border border-neutral-400 bg-white text-neutral-700"
                                                                }`}
                                                            >
                                                                <Trophy size={11} weight="bold" />
                                                                {isWinner ? "Winner" : "Keep"}
                                                            </button>
                                                        )}
                                                        {!isReverted && (
                                                            <button
                                                                type="button"
                                                                data-testid={`impact-revert-${row.id}`}
                                                                disabled={isReverting || isMarking}
                                                                onClick={() =>
                                                                    onRequestRevert(row)
                                                                }
                                                                title="Undo this change on Google Ads"
                                                                className="brutal-btn border border-red-500 bg-white text-red-700 hover:bg-red-50 font-mono uppercase tracking-widest text-[10px] font-bold px-2 h-7 flex items-center gap-1"
                                                            >
                                                                <ArrowUUpLeft size={11} weight="bold" />
                                                                {isReverting ? "…" : "Revert"}
                                                            </button>
                                                        )}
                                                        {isReverted && (
                                                            <span className="font-mono uppercase tracking-widest text-[9px] text-neutral-400">
                                                                {(row.reverted_at || "").slice(0, 10)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const CampaignPicker = ({
    campaigns,
    loading,
    value,
    onChange,
    accountName,
    appliedCount = 0,
    onClearAllApplied,
    clearingAll,
}) => {
    const hasCampaigns = campaigns && campaigns.length > 0;
    const dataCount = (campaigns || []).filter((c) => c.has_data).length;
    return (
        <div
            data-testid="demo-campaign-picker"
            className="border border-neutral-300 bg-neutral-50 p-3 mb-6 flex items-center gap-3 flex-wrap"
        >
            <div className="flex items-center gap-2">
                <Target size={14} weight="bold" className="text-klein" />
                <div className="font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                    Select Campaign
                </div>
            </div>
            <div className="relative flex-1 min-w-[220px] max-w-md">
                <select
                    data-testid="demo-campaign-select"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={loading || !hasCampaigns}
                    className="w-full appearance-none border border-ink bg-white text-ink font-mono text-xs font-bold pl-3 pr-8 h-9 focus:outline-none focus:ring-2 focus:ring-klein/40 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <option value="">
                        {loading
                            ? "Loading campaigns…"
                            : hasCampaigns
                            ? `All campaigns (${campaigns.length})`
                            : "No enabled Search campaigns"}
                    </option>
                    {hasCampaigns &&
                        campaigns.map((c) => (
                            <option key={c.id} value={c.id} disabled={!c.has_data}>
                                {c.name}
                                {c.has_data ? "" : " · no demo data in range"}
                            </option>
                        ))}
                </select>
                <CaretDown
                    size={12}
                    weight="bold"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                />
            </div>
            {value && (
                <button
                    data-testid="demo-campaign-clear"
                    onClick={() => onChange("")}
                    className="brutal-btn border border-neutral-400 bg-white text-neutral-700 font-mono uppercase tracking-widest text-[9px] font-bold px-2 h-9 flex items-center gap-1"
                    title="Show all campaigns"
                >
                    <Trash size={11} weight="bold" />
                    Clear filter
                </button>
            )}
            {appliedCount > 0 && onClearAllApplied && (
                <button
                    data-testid="demo-clear-applied-all"
                    onClick={onClearAllApplied}
                    disabled={clearingAll}
                    className="brutal-btn border border-red-500 bg-white text-red-600 hover:bg-red-500 hover:text-white font-mono uppercase tracking-widest text-[9px] font-bold px-2 h-9 flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    title="Remove ALL applied records for this account so recommendations become actionable again. Does NOT reverse live Ads changes."
                >
                    {clearingAll ? (
                        <ArrowsClockwise
                            size={11}
                            weight="bold"
                            className="animate-spin"
                        />
                    ) : (
                        <Trash size={11} weight="bold" />
                    )}
                    Clear {appliedCount} applied
                </button>
            )}
            <div className="font-mono text-[10px] text-neutral-500 ml-auto">
                {loading
                    ? "…"
                    : hasCampaigns
                    ? `${dataCount} of ${campaigns.length} with data · ${accountName || ""}`
                    : accountName || ""}
            </div>
        </div>
    );
};

const RecommendationsPanel = ({
    recs,
    loading,
    appliedMap,
    applyingKey,
    onApply,
    buildKey,
    selectedCampaign,
}) => (
    <div
        data-testid="demo-recommendations"
        className="border border-klein bg-klein/5 p-4 mb-8"
    >
        <div className="flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="bold" className="text-klein" />
            <h2 className="font-heading text-sm font-black uppercase tracking-tight text-ink">
                AI Recommendations
            </h2>
            {loading && (
                <ArrowsClockwise
                    size={12}
                    weight="bold"
                    className="text-klein animate-spin ml-1"
                />
            )}
        </div>
        {!loading && recs.length === 0 ? (
            <p className="text-sm text-neutral-500">
                No high-impact opportunities detected — targeting looks tight, or waiting for data.
            </p>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {recs.map((r, i) => {
                    const meta = ACTION_META[r.action] || ACTION_META.monitor;
                    const Icon = meta.icon;
                    const key = buildKey(r.dimension, r.bucket, selectedCampaign);
                    const applied = appliedMap?.[key];
                    const isApplying = applyingKey === key;
                    const actionable = r.action !== "monitor";
                    return (
                        <div
                            key={i}
                            data-testid={`demo-rec-${i}`}
                            className={`border p-3 flex items-start gap-3 ${meta.cls}`}
                        >
                            <Icon size={18} weight="bold" className="mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono uppercase tracking-widest text-[9px] font-bold">
                                        {r.dimension}
                                    </span>
                                    <span className="font-heading font-black text-sm">{r.bucket}</span>
                                    <span className="font-mono uppercase tracking-widest text-[10px] font-bold px-1.5 py-0.5 bg-white">
                                        {meta.label}
                                        {r.suggested_bid_pct > 0 &&
                                            ` · ${r.action === "decrease_bid" ? "-" : "+"}${Math.abs(r.suggested_bid_pct)}%`}
                                    </span>
                                </div>
                                <div className="mt-1 text-xs leading-relaxed">{r.reason}</div>
                                {actionable && (
                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                        {applied ? (
                                            <span
                                                data-testid={`demo-rec-${i}-applied`}
                                                className="inline-flex items-center gap-1 border border-emerald-600 bg-emerald-600 text-white font-mono uppercase tracking-widest text-[9px] font-bold px-2 py-1"
                                                title={
                                                    applied.pushed_live
                                                        ? `Pushed to ${applied.campaigns_updated} campaign(s) · ${(
                                                              applied.applied_at || ""
                                                          ).slice(0, 10)}`
                                                        : `CSV fallback · ${(applied.applied_at || "").slice(0, 10)}`
                                                }
                                            >
                                                <CheckCircle size={10} weight="bold" />
                                                Applied
                                                {applied.pushed_live ? "" : " (CSV)"}
                                            </span>
                                        ) : (
                                            <button
                                                data-testid={`demo-rec-${i}-apply`}
                                                onClick={() => onApply(r, i)}
                                                disabled={isApplying}
                                                className="inline-flex items-center gap-1 border border-ink bg-ink text-white hover:bg-klein hover:border-klein disabled:opacity-60 disabled:cursor-not-allowed font-mono uppercase tracking-widest text-[9px] font-bold px-2 py-1 transition-colors"
                                            >
                                                {isApplying ? (
                                                    <>
                                                        <ArrowsClockwise
                                                            size={10}
                                                            weight="bold"
                                                            className="animate-spin"
                                                        />
                                                        Syncing…
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkle size={10} weight="bold" />
                                                        Apply Recommendation
                                                    </>
                                                )}
                                            </button>
                                        )}
                                        {applied && !applied.pushed_live && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-amber-700"
                                                title={applied.push_error || ""}
                                            >
                                                <Warning size={10} weight="bold" />
                                                Editor CSV
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        )}
    </div>
);

const DimensionSection = ({ dim, data, recs, appliedMap, buildKey, selectedCampaign }) => {
    const meta = DIM_META[dim];
    const Icon = meta.icon;
    const rows = data ? orderBuckets(data.summary || [], dim) : [];
    return (
        <section
            data-testid={`demo-section-${dim}`}
            className="border border-neutral-300 bg-white"
        >
            <div className="flex items-center justify-between p-4 border-b-2 border-ink flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Icon size={18} weight="bold" className="text-klein" />
                    <h2 className="font-heading text-lg font-black uppercase tracking-tight text-ink">
                        {meta.label}
                    </h2>
                    <span className="mono-label ml-2">{rows.length} buckets</span>
                </div>
                {recs.length > 0 && (
                    <span className="mono-label text-klein">
                        {recs.length} AI insight{recs.length === 1 ? "" : "s"}
                    </span>
                )}
            </div>
            {rows.length === 0 ? (
                <div className="p-8 text-center text-neutral-500 text-sm">
                    No data for this dimension.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-100">
                            <tr className="text-left">
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold">
                                    Bucket
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Impressions
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Clicks
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    CTR
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Cost
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right">
                                    Conv.
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right whitespace-nowrap">
                                    Conv Rate
                                </th>
                                <th className="px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-neutral-600 font-bold text-right whitespace-nowrap">
                                    Cost / Conv.
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const rowBucket = (row?.bucket || "").toLowerCase();
                                const bucketRec = rowBucket
                                    ? recs.find(
                                          (r) =>
                                              (r?.bucket || "").toLowerCase() === rowBucket
                                      )
                                    : null;
                                const actionMeta = bucketRec
                                    ? ACTION_META[bucketRec.action]
                                    : null;
                                const applied = buildKey
                                    ? appliedMap?.[buildKey(dim, row.bucket, selectedCampaign)]
                                    : null;
                                return (
                                    <tr
                                        key={row.bucket}
                                        className="border-t border-neutral-200 hover:bg-neutral-50"
                                    >
                                        <td className="px-3 py-2 text-ink font-medium">
                                            {row.bucket}
                                            {actionMeta && (
                                                <span
                                                    className={`ml-2 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold border ${actionMeta.cls}`}
                                                >
                                                    {actionMeta.label}
                                                </span>
                                            )}
                                            {applied && (
                                                <span
                                                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold border border-emerald-600 bg-emerald-600 text-white"
                                                    title={
                                                        applied.pushed_live
                                                            ? `Pushed to ${applied.campaigns_updated} campaign(s)`
                                                            : "Applied via CSV fallback"
                                                    }
                                                >
                                                    <CheckCircle size={9} weight="bold" />
                                                    Applied
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {row.impressions.toLocaleString()}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {row.clicks}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {row.ctr.toFixed(1)}%
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {row.cost.toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {row.conversions.toFixed(1)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink">
                                            {row.conv_rate.toFixed(1)}%
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-ink whitespace-nowrap">
                                            {row.cost_per_conv != null
                                                ? row.cost_per_conv.toFixed(2)
                                                : <span className="text-neutral-400">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
